import type { Tag, DeviceTag, TagMeta, ConfigTag, LinkTag } from '@nfc-api/tag';
import type { OnboardClientConfig } from '@nfc-api/config';
import useApiClient, { useRequestInit } from './apiClient';
import type { UserRequest } from '@user-api/userProfile';
import type { OrganizationForm } from '@nfc-api/user';

export const useTagApi = (
  baseUrl: string,
  ssr = false,
  sessionCookie: string | null = null
) => {
  const apiClient = useApiClient(ssr);
  const getRequestInit = useRequestInit(ssr, sessionCookie);

  const getDeviceTag = async (uuid: string) =>
    await apiClient<DeviceTag>(
      `${baseUrl}/api/v1/nfc/tag/${uuid}/view`,
      getRequestInit({
        method: 'GET',
      })
    );

  const getConfigTag = async (uuid: string) =>
    await apiClient<ConfigTag>(
      `${baseUrl}/api/v1/nfc/tag/${uuid}/config`,
      getRequestInit({
        method: 'GET',
      })
    );

  const getTag = async (uuid: string) =>
    await apiClient<Tag>(
      `${baseUrl}/api/v1/nfc/tag/${uuid}`,
      getRequestInit({
        method: 'GET',
      })
    );

  const getTagMeta = async (uuid: string) =>
    await apiClient<TagMeta>(
      `${baseUrl}/api/v1/nfc/tag/${uuid}/meta`,
      getRequestInit({
        method: 'GET',
      })
    );

  const getLinkTag = async (uuid: string) =>
    await apiClient<LinkTag>(
      `${baseUrl}/api/v1/nfc/tag/${uuid}/view`,
      getRequestInit({
        method: 'GET',
      })
    );

  const sendUserConfig = async (uuid: string, onboardClientConfig: OnboardClientConfig) =>
    await apiClient(
      `${baseUrl}/api/v1/nfc/tag/${uuid}/config`,
      getRequestInit({
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(onboardClientConfig),
      }),
      false
    );

  const userExists = async (uuid: string, email: string) =>
    await apiClient<{ userFound: boolean }>(
      `${baseUrl}/api/v1/nfc/user/${uuid}/${email}`,
      getRequestInit({
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })
    );

  const createAccount = async (uuid: string, user: UserRequest, organization: OrganizationForm) => 
    await apiClient(
      `${baseUrl}/api/v1/nfc/user/${uuid}/createAccount`,
      getRequestInit({
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user, organization })
      }),
      false
    );

  return {
    getDeviceTag,
    getTagMeta,
    getConfigTag,
    getTag,
    getLinkTag,
    sendUserConfig,
    userExists,
    createAccount
  };
};
